import React, { memo, useEffect, useRef, useState } from 'react';
import type { Color } from 'packages/ds/ColorPicker/types';
import { ColorPicker, type ColorPickerTriggerProps } from 'packages/ds/ColorPicker/ColorPicker';
import { ColorPickerTrigger, type ColorPickerTriggerRef } from './ColorPickerTrigger';
import { arePropsEqual, type Props } from './shared';

const ColorInputLegacy = memo<Props>(
    ({
        isMixed,
        id,
        value: defaultValue,
        onChange,
        label,
        testId,
        isDisabled,
        isGradientDisabled,
        isLeftPositionAllowed,
    }: Props) => {
        const colorPickerTriggerRef = useRef<ColorPickerTriggerRef | null>(null);
        const [isColorDragging, setColorDragging] = useState(false);
        const [value, setValue] = useState<Color>(() =>
            typeof defaultValue === 'string'
                ? {
                      color: defaultValue,
                      opacity: 100,
                  }
                : defaultValue,
        );

        const onColorChange = (newValue: Color) => {
            colorPickerTriggerRef.current?.setValue(newValue);
            onChange(newValue);
        };

        useEffect(() => {
            // @ts-ignore
            if (value?.type !== defaultValue?.type) {
                setValue(defaultValue);
            } else if (!isColorDragging) {
                setValue(defaultValue);
            }
        }, [defaultValue]);

        return (
            <ColorPicker
                value={value}
                setColorDragging={setColorDragging}
                isColorDragging={isColorDragging}
                onChange={onColorChange}
                isColorTypeChangeAllowed={!isGradientDisabled}
                isLeftPositionAllowed={isLeftPositionAllowed}
                trigger={({ onClick, isOpen, ref, ...triggerProps }: ColorPickerTriggerProps) => (
                    <ColorPickerTrigger
                        ref={colorPickerTriggerRef}
                        onClick={onClick}
                        isColorDragging={isColorDragging}
                        onChange={onColorChange}
                        triggerRef={ref}
                        triggerProps={triggerProps}
                        defaultValue={value}
                        isMixed={isMixed}
                        id={id}
                        label={label}
                        testId={testId}
                        isDisabled={isDisabled}
                    />
                )}
            />
        );
    },
    arePropsEqual,
);

export default ColorInputLegacy;
